import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

const MatrixSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeQuadrant, setActiveQuadrant] = useState(0);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const showDiagnosticSection = false; // Set to true to show the diagnostic quiz
  
  // Interactive diagnostic state
  const [peaceAnswers, setPeaceAnswers] = useState([null, null, null, null, null]);
  const [bondAnswers, setBondAnswers] = useState([null, null, null, null, null]);
  const [showResults, setShowResults] = useState(false);

  // Calculate scores
  const peaceScore = peaceAnswers.reduce((sum, val) => sum + (val ?? 0), 0);
  const bondScore = bondAnswers.reduce((sum, val) => sum + (val ?? 0), 0);
  const allAnswered = peaceAnswers.every(a => a !== null) && bondAnswers.every(a => a !== null);

  // Determine quadrant based on scores
  const getResultQuadrant = () => {
    const highPeace = peaceScore >= 10;
    const highBond = bondScore >= 10;
    
    if (!highPeace && !highBond) return 0; // Zone 1 - Stressed & Distant
    if (!highPeace && highBond) return 1;  // Zone 2 - Connected but Volatile
    if (highPeace && !highBond) return 2;  // Zone 3 - Calm but Distant
    return 3; // Zone 4 - Aligned & Deeply Connected
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Content from THE PEACE – BOND RELATIONSHIP MATRIX PDF
  // Ordered as Zone 1, Zone 2, Zone 3, Zone 4 for sequential navigation
  const quadrants = [
    {
      id: 'zone1',
      label: 'Zone 1',
      axes: 'Low Peace × Low Bond',
      tagline: 'Stressed & Distant',
      relationshipType: 'Butting Heads',
      parentType: 'The Power-Struggle Parent',
      characteristics: [
        'Frequent conflict or cold withdrawal',
        'Strong ego involvement',
        'Little emotional safety',
        'Both sides feel misunderstood'
      ],
      commonLanguage: [
        "Why can't you just listen?",
        "You never understand.",
        "I'm tired of explaining."
      ],
      observableMarkers: [
        'Raised voice or sharp tone',
        'Defensive body language',
        'Avoidance after conflict',
        'Emotional residue lingers'
      ],
      temperature: '🔥 Hot & Unsafe',
      silenceSignature: '✖ Charged Silence',
      silenceNote: 'Silence feels tense, hostile, or avoidant.',
      diagnosis: [
        'High MindWeight',
        'High physiological restlessness',
        'Low awareness during interaction'
      ],
      direction: 'Increase Peace first',
      directionNote: '(Do NOT chase bonding yet)',
      practices: [
        'Physiological Restlessness Reset',
        'Speech regulation (quantity, tone and velocity)',
        'Reaction-delay practices',
        'Contemplation on relevant principles (Urge to prove, right vs wrong)'
      ]
    },
    {
      id: 'zone2',
      label: 'Zone 2',
      axes: 'Low Peace × High Bond',
      tagline: 'Connected but Volatile',
      relationshipType: 'Close but clashing',
      parentType: 'The Rollercoaster Parent',
      characteristics: [
        'Strong emotional bond',
        'Frequent arguments or emotional spikes',
        'Big feelings, quick repair, repeated cycles'
      ],
      commonLanguage: [
        "You know I care!",
        "Why does this hurt so much?",
        "We're close, but it's exhausting."
      ],
      observableMarkers: [
        'Emotional highs and lows',
        'Intense conversations',
        'Frequent apologies',
        'Repeating patterns'
      ],
      temperature: '🔥 Hot & Intimate',
      silenceSignature: '⚡ Fragile Silence',
      silenceNote: 'Silence exists briefly but feels unstable.',
      diagnosis: [
        'High emotional connection',
        'Inconsistent behavior regulation',
        'Desire to be seen / validated / right / in control'
      ],
      direction: 'Increase Peace without reducing Bond',
      directionNote: '',
      practices: [
        'Emotional regulation exercises',
        'Trigger resistance',
        'Speech regulation (quantity, tone and velocity)',
        'Contemplation on relevant principles'
      ]
    },
    {
      id: 'zone3',
      label: 'Zone 3',
      axes: 'High Peace × Low Bond',
      tagline: 'Calm but Distant',
      relationshipType: 'Nice but Empty',
      parentType: 'The Polite Manager',
      characteristics: [
        'Calm, respectful, controlled',
        'Minimal conflict',
        'Emotional distance',
        'Relationship feels formal'
      ],
      commonLanguage: [
        "How can I help you?",
        "I understand how you feel.",
        "Let me know if you need anything."
      ],
      observableMarkers: [
        'Transactional conversations',
        'Low spontaneity',
        'Child rarely initiates',
        'Low laughter'
      ],
      temperature: '❄️ Cool & Stable',
      silenceSignature: '▢ Neutral Silence',
      silenceNote: 'Low stress, but does not create bonding.',
      diagnosis: [
        'Focus on emotional correctness over warmth',
        'Logical over loving',
        'Physical presence without mental connection',
        'Feel like strangers / acquaintances',
        'Warmth is under-expressed'
      ],
      direction: 'Increase Bond without losing Peace',
      directionNote: '',
      practices: [
        'Speech substitution (formal to warm)',
        'Non-goal-oriented time together',
        'Contemplation on relevant principles',
        'Physical micro-gestures of warmth'
      ]
    },
    {
      id: 'zone4',
      label: 'Zone 4',
      axes: 'High Peace × High Bond',
      tagline: 'Aligned & Deeply Connected',
      relationshipType: 'Silent Besties',
      parentType: 'The Quiet Parent',
      characteristics: [
        'Calm and peaceful',
        'Deep trust',
        'No need to fix or manage',
        'Relationship feels natural and deep'
      ],
      commonLanguage: [
        "Often nothing",
        "Simple, natural sharing",
        "Natural humour"
      ],
      observableMarkers: [
        'Child initiates connection',
        'Easy physical presence',
        'Slow, relaxed interactions',
        'No emotional residue'
      ],
      temperature: '☀️ Warm & Connected',
      silenceSignature: '😊 Nourishing Silence',
      silenceNote: 'Silence deepens the bond. Often, being together is enough. No words needed.',
      diagnosis: [
        'Almost no stress or conflict',
        'High emotional awareness & regulation',
        'High alignment and comfort with each other'
      ],
      direction: 'Sustain Presence',
      directionNote: '',
      practices: [
        'Shared silent retreats',
        'Advanced Contemplation exercises'
      ]
    }
  ];

  // Grid indices for matrix display (Zone 2 top-left, Zone 4 top-right, Zone 1 bottom-left, Zone 3 bottom-right)
  const gridIndices = {
    topLeft: 1,     // Zone 2
    topRight: 3,    // Zone 4
    bottomLeft: 0,  // Zone 1
    bottomRight: 2  // Zone 3
  };

  const currentQuadrant = quadrants[activeQuadrant];

  const nextQuadrant = () => {
    setActiveQuadrant((prev) => (prev + 1) % quadrants.length);
  };

  const prevQuadrant = () => {
    setActiveQuadrant((prev) => (prev - 1 + quadrants.length) % quadrants.length);
  };

  return (
    <section 
      ref={sectionRef}
      id="peace-bond-matrix"
      className="py-32 bg-[#efede6] relative overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <span className={`inline-block text-sm text-[#202020]/40 uppercase tracking-widest mb-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            Diagnostic Tool
          </span>
          <h2 className={`text-3xl md:text-4xl lg:text-5xl font-normal text-[#202020] mb-8 tracking-tight transition-all duration-1000 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            The Peace–Bond Relationship Matrix
          </h2>
          <p className={`text-xl text-[#202020]/50 max-w-2xl mx-auto transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            Understand where your relationship currently sits on two axes:<br/>{' '}
            <strong className="text-[#202020]">Peacefulness</strong> and{' '}
            <strong className="text-[#202020]">Emotional Bond</strong>.
          </p>
        </div>

        {/* Visual Matrix Grid - Large Overview */}
        <div className={`mb-16 w-full max-w-4xl mx-auto transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="grid grid-cols-[auto_1fr_1fr] gap-4">
            {/* Top labels */}
            <div></div>
            <div className="text-center pb-4">
              <p className="text-lg text-[#202020]/50 font-medium">Low Peace</p>
            </div>
            <div className="text-center pb-4">
              <p className="text-lg text-[#202020]/50 font-medium">High Peace</p>
            </div>
            
            {/* Row 1: High Bond */}
            <div className="flex items-center justify-end pr-4">
              <p className="text-lg text-[#202020]/50 font-medium">High Bond</p>
            </div>
            <button 
              onClick={() => setActiveQuadrant(gridIndices.topLeft)}
              className={`aspect-[4/3] rounded-2xl p-6 flex flex-col items-center justify-center text-center transition-all ${activeQuadrant === gridIndices.topLeft ? 'bg-[#202020] text-[#efede6] shadow-lg' : 'bg-white hover:bg-white/80 text-[#202020] hover:shadow-md'}`}
            >
              <p className="text-lg opacity-60 font-bold mb-2">Zone 2</p>
              <p className="font-semibold text-base md:text-2xl leading-tight">{quadrants[gridIndices.topLeft].tagline}</p>
            </button>
            <button 
              onClick={() => setActiveQuadrant(gridIndices.topRight)}
              className={`aspect-[4/3] rounded-2xl p-6 flex flex-col items-center justify-center text-center transition-all ${activeQuadrant === gridIndices.topRight ? 'bg-[#202020] text-[#efede6] shadow-lg' : 'bg-white hover:bg-white/80 text-[#202020] hover:shadow-md'}`}
            >
              <p className="text-lg opacity-60 font-bold mb-2">Zone 4</p>
              <p className="font-semibold text-base md:text-2xl leading-tight">{quadrants[gridIndices.topRight].tagline}</p>
            </button>
            
            {/* Row 2: Low Bond */}
            <div className="flex items-center justify-end pr-4">
              <p className="text-lg text-[#202020]/50 font-medium">Low Bond</p>
            </div>
            <button 
              onClick={() => setActiveQuadrant(gridIndices.bottomLeft)}
              className={`aspect-[4/3] rounded-2xl p-6 flex flex-col items-center justify-center text-center transition-all ${activeQuadrant === gridIndices.bottomLeft ? 'bg-[#202020] text-[#efede6] shadow-lg' : 'bg-white hover:bg-white/80 text-[#202020] hover:shadow-md'}`}
            >
              <p className="text-lg opacity-60 font-bold mb-2">Zone 1</p>
              <p className="font-semibold text-base md:text-2xl leading-tight">{quadrants[gridIndices.bottomLeft].tagline}</p>
            </button>
            <button 
              onClick={() => setActiveQuadrant(gridIndices.bottomRight)}
              className={`aspect-[4/3] rounded-2xl p-6 flex flex-col items-center justify-center text-center transition-all ${activeQuadrant === gridIndices.bottomRight ? 'bg-[#202020] text-[#efede6] shadow-lg' : 'bg-white hover:bg-white/80 text-[#202020] hover:shadow-md'}`}
            >
              <p className="text-lg opacity-60 font-bold mb-2">Zone 3</p>
              <p className="font-semibold text-base md:text-2xl leading-tight">{quadrants[gridIndices.bottomRight].tagline}</p>
            </button>
          </div>
          <p className="text-center text-base text-[#202020]/50 mt-6">Click any quadrant to learn more</p>
        </div>

        {/* Active Quadrant Detail Card */}
        <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            {/* Card Header */}
            <div className="p-8 md:p-10 border-b border-[#202020]/10">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold px-3 py-1 rounded-lg bg-[#202020] text-[#efede6]">
                    {currentQuadrant.label}
                  </span>
                  <span className="text-sm text-[#202020]/50">{currentQuadrant.axes}</span>
                </div>
                
                {/* Navigation */}
                <div className="flex items-center gap-3">
                  <button 
                    onClick={prevQuadrant}
                    className="w-10 h-10 rounded-lg border border-[#202020]/20 flex items-center justify-center hover:bg-[#202020] hover:text-[#efede6] hover:border-[#202020] transition-all"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={nextQuadrant}
                    className="w-10 h-10 rounded-lg border border-[#202020]/20 flex items-center justify-center hover:bg-[#202020] hover:text-[#efede6] hover:border-[#202020] transition-all"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <h3 className="text-2xl md:text-3xl font-normal text-[#202020] mb-2">{currentQuadrant.tagline}</h3>
              <p className="text-[#202020]/60">
                {currentQuadrant.relationshipType} • {currentQuadrant.parentType}
              </p>
            </div>

            {/* Card Content */}
            <div className="p-8 md:p-10">
              <div className="grid md:grid-cols-2 gap-10">
                {/* Left Column */}
                <div className="space-y-8">
                  {/* Temperature & Silence */}
                  <div className="flex flex-wrap gap-3">
                    <span className="px-4 py-2 bg-[#efede6] rounded-xl text-sm text-[#202020]">{currentQuadrant.temperature}</span>
                    <span className="px-4 py-2 bg-[#efede6] rounded-xl text-sm text-[#202020]">{currentQuadrant.silenceSignature}</span>
                  </div>
                  
                  <p className="text-[#202020]/60 italic">{currentQuadrant.silenceNote}</p>

                  {/* Characteristics */}
                  <div>
                    <h4 className="text-sm font-medium text-[#202020]/50 uppercase tracking-wider mb-4">
                      Characteristics
                    </h4>
                    <ul className="space-y-2">
                      {currentQuadrant.characteristics.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-[#202020]/80">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#202020]/30 mt-2 flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Common Language */}
                  <div>
                    <h4 className="text-sm font-medium text-[#202020]/50 uppercase tracking-wider mb-4">
                      Common Language
                    </h4>
                    <div className="space-y-2">
                      {currentQuadrant.commonLanguage.map((item, i) => (
                        <p key={i} className="text-[#202020]/70 italic pl-4 border-l-2 border-[#202020]/20">
                          "{item}"
                        </p>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-8">
                  {/* Observable Markers */}
                  <div>
                    <h4 className="text-sm font-medium text-[#202020]/50 uppercase tracking-wider mb-4">
                      Observable Markers
                    </h4>
                    <ul className="space-y-2">
                      {currentQuadrant.observableMarkers.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-[#202020]/80">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#202020]/30 mt-2 flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Contemplate Diagnosis */}
                  <div>
                    <h4 className="text-sm font-medium text-[#202020]/50 uppercase tracking-wider mb-4">
                      Contemplate Diagnosis
                    </h4>
                    <ul className="space-y-2">
                      {currentQuadrant.diagnosis.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-[#202020]/80">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#202020]/30 mt-2 flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Direction & Practices */}
              <div className="mt-10 pt-8 border-t border-[#202020]/10">
                <div className="bg-[#202020] rounded-2xl p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <ArrowRight className="w-5 h-5 text-[#efede6]" />
                    <h4 className="text-lg font-medium text-[#efede6]">
                      {currentQuadrant.direction}
                      {currentQuadrant.directionNote && (
                        <span className="text-[#efede6]/60 text-sm ml-2">{currentQuadrant.directionNote}</span>
                      )}
                    </h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {currentQuadrant.practices.map((item, i) => (
                      <span key={i} className="px-3 py-1.5 bg-[#efede6]/20 rounded-lg text-sm text-[#efede6]">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Dots Navigation */}
            <div className="flex items-center justify-center gap-2 pb-8">
              {quadrants.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveQuadrant(index)}
                  className={`h-1.5 rounded-lg transition-all ${index === activeQuadrant ? 'bg-[#202020] w-8' : 'bg-[#202020]/20 w-1.5 hover:bg-[#202020]/40'}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Diagnostic Questions - Collapsible (Hidden for now) */}
        {showDiagnosticSection && (
        <div className={`mt-16 bg-white rounded-3xl overflow-hidden transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <button
            onClick={() => setShowDiagnostics(!showDiagnostics)}
            className="w-full flex items-center justify-between p-8 md:p-10 text-left group"
          >
            <div>
              <h3 className="text-2xl md:text-3xl font-normal text-[#202020] mb-2">
                What parenting zone are you in?
              </h3>
              <p className="text-[#202020]/50">
                Answer a few questions to find your parenting zone.
              </p>
            </div>
            <ChevronDown 
              className={`w-6 h-6 text-[#202020]/40 flex-shrink-0 transition-transform duration-300 ${showDiagnostics ? 'rotate-180' : ''}`}
            />
          </button>

          <div 
            className={`overflow-hidden transition-all duration-300 ${showDiagnostics ? 'max-h-[2000px] pb-8 md:pb-10' : 'max-h-0'}`}
          >
            <div className="px-8 md:px-10 space-y-10">
              {/* Peacefulness Score */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xl font-normal text-[#202020]">
                    Peacefulness Score
                  </h4>
                  <span className="text-2xl font-bold text-[#202020]">{peaceScore}/20</span>
                </div>
                <p className="text-sm text-[#202020]/50 mb-6">
                  Rate each from 0 (Not at all) to 4 (Very much).
                </p>
                <div className="space-y-3">
                  {[
                    'Conversations stay calm even during disagreement',
                    'Arguments de-escalate quickly',
                    'My body feels relaxed around this person',
                    'Emotional recovery is fast after interaction',
                    'There is little lingering tension'
                  ].map((statement, i) => (
                    <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-[#efede6] rounded-xl">
                      <div className="flex items-center gap-3 flex-1">
                        <span className="w-6 h-6 rounded-full bg-[#202020]/10 flex items-center justify-center text-xs font-bold text-[#202020]/60 flex-shrink-0">
                          {i + 1}
                        </span>
                        <span className="text-[#202020]/70 text-sm sm:text-base">{statement}</span>
                      </div>
                      <div className="flex gap-2 ml-9 sm:ml-0">
                        {[0, 1, 2, 3, 4].map((value) => (
                          <button
                            key={value}
                            onClick={() => {
                              const newAnswers = [...peaceAnswers];
                              newAnswers[i] = value;
                              setPeaceAnswers(newAnswers);
                            }}
                            className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                              peaceAnswers[i] === value 
                                ? 'bg-[#202020] text-[#efede6]' 
                                : 'bg-white hover:bg-[#202020]/10 text-[#202020]/70'
                            }`}
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Emotional Bond Score */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xl font-normal text-[#202020]">
                    Emotional Bond Score
                  </h4>
                  <span className="text-2xl font-bold text-[#202020]">{bondScore}/20</span>
                </div>
                <p className="text-sm text-[#202020]/50 mb-6">
                  Rate each from 0 (Not at all) to 4 (Very much).
                </p>
                <div className="space-y-3">
                  {[
                    'We enjoy spending time together without a goal',
                    'They share thoughts or feelings voluntarily',
                    'I feel emotionally close to them',
                    'There is warmth or affection in our interactions',
                    'Silence together feels comfortable'
                  ].map((statement, i) => (
                    <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-[#efede6] rounded-xl">
                      <div className="flex items-center gap-3 flex-1">
                        <span className="w-6 h-6 rounded-full bg-[#202020]/10 flex items-center justify-center text-xs font-bold text-[#202020]/60 flex-shrink-0">
                          {i + 1}
                        </span>
                        <span className="text-[#202020]/70 text-sm sm:text-base">{statement}</span>
                      </div>
                      <div className="flex gap-2 ml-9 sm:ml-0">
                        {[0, 1, 2, 3, 4].map((value) => (
                          <button
                            key={value}
                            onClick={() => {
                              const newAnswers = [...bondAnswers];
                              newAnswers[i] = value;
                              setBondAnswers(newAnswers);
                            }}
                            className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                              bondAnswers[i] === value 
                                ? 'bg-[#202020] text-[#efede6]' 
                                : 'bg-white hover:bg-[#202020]/10 text-[#202020]/70'
                            }`}
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Results Section */}
              {allAnswered && (
                <div className="p-6 md:p-8 bg-[#202020] rounded-2xl text-[#efede6]">
                  <h4 className="text-xl md:text-2xl font-medium mb-4">Your Results</h4>
                  <div className="grid sm:grid-cols-2 gap-4 mb-6">
                    <div className="p-4 bg-[#efede6]/10 rounded-xl">
                      <p className="text-sm text-[#efede6]/60 mb-1">Peace Score</p>
                      <p className="text-3xl font-bold">{peaceScore}/20</p>
                      <p className="text-sm text-[#efede6]/60 mt-1">
                        {peaceScore >= 10 ? '✓ High Peace' : '○ Low Peace'}
                      </p>
                    </div>
                    <div className="p-4 bg-[#efede6]/10 rounded-xl">
                      <p className="text-sm text-[#efede6]/60 mb-1">Bond Score</p>
                      <p className="text-3xl font-bold">{bondScore}/20</p>
                      <p className="text-sm text-[#efede6]/60 mt-1">
                        {bondScore >= 10 ? '✓ High Bond' : '○ Low Bond'}
                      </p>
                    </div>
                  </div>
                  <div className="border-t border-[#efede6]/20 pt-6">
                    <p className="text-sm text-[#efede6]/60 mb-2">You are in</p>
                    <p className="text-2xl md:text-3xl font-semibold mb-2">
                      {quadrants[getResultQuadrant()].tagline}
                    </p>
                    <p className="text-[#efede6]/70 mb-4">
                      {quadrants[getResultQuadrant()].relationshipType} • {quadrants[getResultQuadrant()].parentType}
                    </p>
                    <button
                      onClick={() => setActiveQuadrant(getResultQuadrant())}
                      className="px-6 py-3 bg-[#efede6] text-[#202020] rounded-xl font-medium hover:bg-[#efede6]/90 transition-all"
                    >
                      View Details for Your Zone
                    </button>
                  </div>
                </div>
              )}

              {!allAnswered && (
                <div className="p-6 bg-[#efede6] rounded-xl text-center">
                  <p className="text-[#202020]/60">
                    Answer all questions above to see your results.
                  </p>
                  <p className="text-sm text-[#202020]/40 mt-2">
                    {10 - (peaceAnswers.filter(a => a !== null).length + bondAnswers.filter(a => a !== null).length)} questions remaining
                  </p>
                </div>
              )}

              {/* Summary Aphorisms */}
              <div className="space-y-4 pt-8 border-t border-[#202020]/10 text-center">
                <p className="text-lg text-[#202020] font-light italic">
                  Peace without bond feels empty.
                </p>
                <p className="text-lg text-[#202020] font-light italic">
                  Bond without peace feels exhausting.
                </p>
                <p className="text-lg text-[#202020] font-light italic">
                  Peace with bond feels complete.
                </p>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </section>
  );
};

export default MatrixSection;
